import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Filter1Icon from '@mui/icons-material/Filter1';
import LinkIcon from '@mui/icons-material/Link';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { SvgIconProps } from '@mui/material';
import { defineMessages, MessageDescriptor } from 'react-intl';

/**
 * Centralized report type translation messages.
 * Use with FormattedMessage or formatMessage so the extractor picks up the ids and defaultMessages.
 *
 * @example
 * <FormattedMessage {...reportNameMessages[reportType]} />
 * formatMessage(reportNameMessages[reportType])
 */
export const reportNameMessages: Record<string, MessageDescriptor> = defineMessages({
  AUDIT_GIP_STATUS: {
    id: 'audit.reportNames.AUDIT_GIP_STATUS',
    defaultMessage: 'GIP',
  },
  AUDIT_GIP_ALTERNATIVE_REPORT: {
    id: 'audit.reportNames.AUDIT_GIP_ALTERNATIVE_REPORT',
    defaultMessage: 'GIP alternativo',
  },
  AUDIT_BROADCAST_EVIDENCE: {
    id: 'audit.reportNames.AUDIT_BROADCAST_EVIDENCE',
    defaultMessage: 'Broadcast Evidence',
  },
  BCR_MATCHES: {
    id: 'audit.reportNames.BCR_MATCHES',
    defaultMessage: 'BCR Matches',
  },
});

/** Report types shown as chips on the saved reports list. */
export const SAVED_REPORT_LIST_TYPES = [
  'AUDIT_GIP_STATUS',
  'AUDIT_GIP_ALTERNATIVE_REPORT',
  'AUDIT_BROADCAST_EVIDENCE',
] as const;

export type SavedReportListType = (typeof SAVED_REPORT_LIST_TYPES)[number];

/** Path segments used by ChipNavigationMui for saved reports. */
export const SAVED_REPORT_LIST_PATH_SEGMENTS = ['gip', 'gip-alt', 'broadcast-evidence'] as const;

export type SavedReportListPathSegment = (typeof SAVED_REPORT_LIST_PATH_SEGMENTS)[number];

/** Maps a saved-report type to its list URL segment under `/audit/saved-reports`. */
export const SAVED_REPORT_TYPE_TO_PATH_SEGMENT: Record<
  SavedReportListType,
  SavedReportListPathSegment
> = {
  AUDIT_GIP_STATUS: 'gip',
  AUDIT_GIP_ALTERNATIVE_REPORT: 'gip-alt',
  AUDIT_BROADCAST_EVIDENCE: 'broadcast-evidence',
};

/**
 * Available report definition column types.
 */
export const COLUMN_TYPES = [
  'string',
  'number',
  'boolean',
  'date',
  'datetime',
  'time',
  'url',
] as const;

export type ColumnType = (typeof COLUMN_TYPES)[number];

/**
 * Column type translation messages (defaults from es.json).
 * Use with formatMessage so the extractor picks up the ids and defaultMessages.
 *
 * @example
 * formatMessage(columnTypeMessages[type] ?? columnTypeMessages.unknown)
 */
export const columnTypeMessages: Record<string, MessageDescriptor> = defineMessages({
  string: {
    id: 'audit.reportDefinitionEditor.columnType.string',
    defaultMessage: 'Texto',
  },
  number: {
    id: 'audit.reportDefinitionEditor.columnType.number',
    defaultMessage: 'Número',
  },
  boolean: {
    id: 'audit.reportDefinitionEditor.columnType.boolean',
    defaultMessage: 'Booleano',
  },
  date: {
    id: 'audit.reportDefinitionEditor.columnType.date',
    defaultMessage: 'Fecha',
  },
  datetime: {
    id: 'audit.reportDefinitionEditor.columnType.datetime',
    defaultMessage: 'Fecha y hora',
  },
  time: {
    id: 'audit.reportDefinitionEditor.columnType.time',
    defaultMessage: 'Hora',
  },
  url: {
    id: 'audit.reportDefinitionEditor.columnType.url',
    defaultMessage: 'URL',
  },
  unknown: {
    id: 'audit.reportDefinitionEditor.columnType.unknown',
    defaultMessage: 'Desconocido',
  },
});

/**
 * Icons for each column type, used in ColumnTypeChip.
 */
export const COLUMN_TYPE_ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  string: TextFieldsIcon,
  number: Filter1Icon,
  boolean: CheckBoxIcon,
  date: CalendarTodayIcon,
  datetime: CalendarMonthIcon,
  time: AccessTimeIcon,
  url: LinkIcon,
};
